/**
 * Generate a clean 32-character hex UUID without hyphens.
 * 
 * @returns {string}
 */
const generateUUID = () => {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID().replace(/-/g, '');
        }
    } catch (e) {
        // Fallback below
    }

    return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

/**
 * Retrieve or generate UID for action telemetry.
 * Clean 32-character hex UUID without web_ or ext_ prefix.
 * Hierarchy:
 * 1. URL search parameters (?uid=...): Passed by extension (cached to localStorage after stripping prefixes).
 * 2. Cached UID in localStorage: Reused across page reloads and in-app navigation.
 * 3. Fallback for direct visitors: Automatically generate a clean 32-character UUID and persist.
 * 
 * @returns {string}
 */
export const getUid = () => {
    try {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            const urlUid = params.get('uid');
            
            // 1. From URL parameter
            if (urlUid && urlUid.trim()) {
                const cleanUid = urlUid.trim().replace(/^(ext_|web_)/, '');
                if (cleanUid) {
                    try {
                        localStorage.setItem('tab_uid', cleanUid);
                    } catch (e) {
                        // Suppress localStorage quota or security errors
                    }
                    return cleanUid;
                }
            }

            // 2. From cached localStorage
            try {
                const cachedUid = localStorage.getItem('tab_uid');
                if (cachedUid && cachedUid.trim()) {
                    const cleanUid = cachedUid.trim().replace(/^(ext_|web_)/, '');
                    if (cleanUid !== cachedUid) {
                        localStorage.setItem('tab_uid', cleanUid);
                    }
                    return cleanUid;
                }
            } catch (e) {
                // Ignore localStorage read errors
            }

            // 3. Direct web visitor -> auto-generate clean UUID
            const newUid = generateUUID();
            try {
                localStorage.setItem('tab_uid', newUid);
            } catch (e) {
                // Ignore localStorage write errors
            }
            return newUid;
        }
    } catch (e) {
        console.warn('Failed to resolve or generate uid:', e);
    }

    return generateUUID();
};

/**
 * Retrieve traffic source ('extension' or 'web').
 * Priority:
 * 1. URL parameter (?source=extension or ?source=web)
 * 2. Cached in localStorage ('tab_source')
 * 3. Fallback default to 'web'
 * 
 * @returns {string}
 */
export const getSource = () => {
    try {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            const urlSource = params.get('source');

            if (urlSource && urlSource.trim()) {
                const cleanSource = urlSource.trim().toLowerCase();
                if (cleanSource === 'extension' || cleanSource === 'web') {
                    try {
                        localStorage.setItem('tab_source', cleanSource);
                    } catch (e) {
                        // Ignore storage error
                    }
                    return cleanSource;
                }
            }

            // Check cached source
            try {
                const cachedSource = localStorage.getItem('tab_source');
                if (cachedSource && cachedSource.trim()) {
                    const s = cachedSource.trim().toLowerCase();
                    if (s === 'extension' || s === 'web') {
                        return s;
                    }
                }
            } catch (e) {
                // Ignore storage error
            }
        }
    } catch (e) {
        console.warn('Failed to resolve traffic source:', e);
    }

    return 'web';
};

/**
 * Retrieve traffic channel.
 * Hierarchy:
 * 1. URL parameter (?channel=...)
 * 2. Cached in localStorage ('tab_channel')
 * 3. Fallback default to '0000'
 * 
 * @returns {string}
 */
export const getChannel = () => {
    try {
        if (typeof window !== 'undefined' && window.location) {
            const params = new URLSearchParams(window.location.search);
            const urlChannel = params.get('channel');

            if (urlChannel && urlChannel.trim()) {
                const cleanChannel = urlChannel.trim();
                try {
                    localStorage.setItem('tab_channel', cleanChannel);
                } catch (e) {
                    // Ignore storage error
                }
                return cleanChannel;
            }

            // Check cached channel
            try {
                const cachedChannel = localStorage.getItem('tab_channel');
                if (cachedChannel && cachedChannel.trim()) {
                    return cachedChannel.trim();
                }
            } catch (e) {
                // Ignore storage error
            }
        }
    } catch (e) {
        console.warn('Failed to resolve channel:', e);
    }

    return '0000';
};

export const recordActionLog = (actionType, itemType, itemId, extraData = {}, itemName = null) => {
    // Determine the base API URL based on environment or window location
    const apiUrl = process.env.NODE_ENV === 'production' 
        ? '/api/v1/log/action' 
        : 'http://localhost:8080/api/v1/log/action';
        
    const uid = getUid();
    const source = getSource();
    const channel = getChannel();
    const resolvedItemName = itemName || (extraData && extraData.name) || null;

    const payload = JSON.stringify({
        actionType,
        itemType,
        itemId,
        itemName: resolvedItemName,
        extraData: JSON.stringify(extraData),
        uid: uid || null,
        source: source || 'web',
        channel: channel || '0000'
    });

    try {
        // Use sendBeacon for non-blocking telemetry if available
        if (navigator.sendBeacon) {
            // sendBeacon requires a Blob for application/json content type to work correctly in some browsers
            const blob = new Blob([payload], { type: 'application/json' });
            navigator.sendBeacon(apiUrl, blob);
        } else {
            // Fallback to fetch with keepalive
            fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: payload,
                keepalive: true
            }).catch(e => console.warn('Failed to send action log', e));
        }
    } catch (e) {
        console.warn('Error recording action log', e);
    }
};
